import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Badge } from '../atoms/Badge/Badge';
import { Button } from '../atoms/Button/Button';
import { Card } from '../atoms/Card/Card';
import { Grid } from '../atoms/Grid/Grid';
import { Inline } from '../atoms/Inline/Inline';
import { Input } from '../atoms/Input/Input';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Radio } from '../atoms/Radio/Radio';
import { Stack } from '../atoms/Stack/Stack';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { Section } from '../templates/Section/Section';
import { taskKeys } from '../../hooks/useTasks';
import {
  deleteMaintenancePlan,
  equipmentSafetyKey,
  saveMaintenancePlan,
  saveSafetyAnswer,
  type EquipmentSafety,
  type MaintenancePlan,
  type SafetyAnswer,
  type SafetyChecklistEntry,
} from '../../services/equipmentSafetyService';
import './EquipmentSafety.css';

const ANSWERS: readonly SafetyAnswer[] = ['yes', 'no', 'na'];
const MIN_DAYS = 1;
const MAX_DAYS = 3650;

function ChecklistItem({ entry, onAnswer }: {
  entry: SafetyChecklistEntry;
  onAnswer: (key: string, answer: SafetyAnswer) => void;
}) {
  const { t } = useTranslation();
  const base = `equipmentSafety.checklist.items.${entry.key}`;
  const whyId = `safety-item-${entry.key}-why`;
  return (
    <li data-testid={`safety-item-${entry.key}`}>
      <Card className="equipment-safety__card">
        <Stack gap="3">
          <Heading level={3}>{t(`${base}.title`)}</Heading>
          {/* The question names the group. Radios stay enabled while saving, so focus never drops. */}
          <fieldset className="equipment-safety__answers" aria-describedby={entry.answer === 'no' ? whyId : undefined}>
            <legend className="equipment-safety__legend">{t(`${base}.question`)}</legend>
            {ANSWERS.map((answer) => (
              <Radio
                key={answer}
                name={`safety-${entry.key}`}
                label={t(`equipmentSafety.checklist.answers.${answer}`)}
                checked={entry.answer === answer}
                onChange={() => onAnswer(entry.key, answer)}
              />
            ))}
          </fieldset>
          {entry.answer === 'no' && (
            // Announced through the section's LiveRegion when the answer is saved.
            <div id={whyId}>
              <Alert tone="warning" compact live="off" title={t('equipmentSafety.checklist.whyTitle')}>
                <Stack gap="1">
                  <Text>{t(`${base}.why`)}</Text>
                  <Text>{t(`${base}.fix`)}</Text>
                </Stack>
              </Alert>
            </div>
          )}
        </Stack>
      </Card>
    </li>
  );
}

/**
 * "Sicherheits-Check" (Reef Guard #1828): the failures software cannot see.
 * A "no" shows why it matters and what to do — never an alert or e-mail.
 * Answers apply optimistically; with several quick changes the latest wins.
 */
export function SafetyChecklistSection({ aquariumId, safety }: { aquariumId: string; safety: EquipmentSafety }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [announcement, setAnnouncement] = useState('');
  const latest = useRef(0);
  const key = equipmentSafetyKey(aquariumId);
  const save = useMutation({
    mutationFn: ({ item, answer }: { item: string; answer: SafetyAnswer; seq: number }) => saveSafetyAnswer(aquariumId, item, answer),
    onMutate: ({ item, answer }) => {
      queryClient.setQueryData<EquipmentSafety>(key, (old) =>
        old ? { ...old, checklist: old.checklist.map((c) => (c.key === item ? { ...c, answer } : c)) } : old,
      );
    },
    onSuccess: (data, { item, answer, seq }) => {
      if (seq !== latest.current) return; // an older answer — the newer one decides
      queryClient.setQueryData(key, data);
      const saved = String(t('equipmentSafety.checklist.saved', {
        item: t(`equipmentSafety.checklist.items.${item}.title`),
        answer: t(`equipmentSafety.checklist.answers.${answer}`),
      }));
      setAnnouncement(answer === 'no' ? `${saved} ${String(t(`equipmentSafety.checklist.items.${item}.why`))}` : saved);
    },
    onError: () => void queryClient.invalidateQueries({ queryKey: key }),
  });

  const open = safety.checklist.filter((c) => c.answer === null || c.answer === 'no').length;
  return (
    <Section id="safety-checklist" title={t('equipmentSafety.checklist.title')} description={t('equipmentSafety.checklist.description')}>
      <Stack gap="4">
        <LiveRegion visuallyHidden>{announcement}</LiveRegion>
        {save.isError && <Alert tone="danger">{t('equipmentSafety.saveError')}</Alert>}
        {safety.checklist.length === 0 ? (
          <Text color="muted">{t('equipmentSafety.empty')}</Text>
        ) : (
          <>
            <Text color="muted" data-testid="safety-progress">
              {open === 0 ? t('equipmentSafety.checklist.allDone') : t('equipmentSafety.checklist.open', { count: open })}
            </Text>
            <Grid as="ul" minItemWidth="md" gap="4" className="equipment-safety__list">
              {safety.checklist.map((entry) => (
                <ChecklistItem
                  key={entry.key}
                  entry={entry}
                  onAnswer={(item, answer) => {
                    latest.current += 1;
                    save.mutate({ item, answer, seq: latest.current });
                  }}
                />
              ))}
            </Grid>
          </>
        )}
      </Stack>
    </Section>
  );
}

function MaintenanceCard({ plan, aquariumId, busy, focusToken, onSave, onOff }: {
  plan: MaintenancePlan;
  aquariumId: string;
  busy: boolean;
  /** Changes after an action on this card finished: focus returns to its heading. */
  focusToken: number;
  onSave: (plan: MaintenancePlan, days: number) => void;
  onOff: (plan: MaintenancePlan) => void;
}) {
  const { t } = useTranslation();
  const base = `equipmentSafety.maintenance.templates.${plan.key}`;
  const headingId = `maintenance-${plan.key}-title`;
  const stored = String(plan.intervalDays ?? plan.defaultIntervalDays);
  const [value, setValue] = useState(stored);
  // Show the stored interval after a save / switch-off — without re-mounting, so focus can be restored.
  useEffect(() => setValue(stored), [stored]);
  useEffect(() => {
    if (focusToken > 0) document.getElementById(headingId)?.focus();
  }, [focusToken, headingId]);
  const days = Number(value);
  const valid = Number.isInteger(days) && days >= MIN_DAYS && days <= MAX_DAYS;
  const changed = plan.enabled && days !== plan.intervalDays;
  return (
    <li data-testid={`maintenance-${plan.key}`}>
      <Card className="equipment-safety__card">
        <Stack gap="3">
          <Inline gap="2" wrap align="center">
            <Heading level={3} id={headingId} tabIndex={-1} className="equipment-safety__heading">{t(`${base}.title`)}</Heading>
            {plan.enabled
              ? <Badge tone="success">{t('equipmentSafety.maintenance.on')}</Badge>
              : <Badge tone="neutral">{t('equipmentSafety.maintenance.off')}</Badge>}
          </Inline>
          <Text color="secondary">{t(`${base}.description`)}</Text>
          <Input
            id={`maintenance-interval-${plan.key}`}
            type="number"
            inputMode="numeric"
            min={MIN_DAYS}
            max={MAX_DAYS}
            step={1}
            label={t('equipmentSafety.maintenance.interval')}
            helperText={t('equipmentSafety.maintenance.suggested', { count: plan.defaultIntervalDays })}
            error={valid ? undefined : t('equipmentSafety.maintenance.invalid', { min: MIN_DAYS, max: MAX_DAYS })}
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
          <Inline gap="2" wrap>
            {!plan.enabled && (
              <Button variant="secondary" size="sm" disabled={busy || !valid} onClick={() => onSave(plan, days)}>
                {t('equipmentSafety.maintenance.switchOn')}
              </Button>
            )}
            {changed && (
              <Button variant="secondary" size="sm" disabled={busy || !valid} onClick={() => onSave(plan, days)}>
                {t('equipmentSafety.maintenance.saveInterval')}
              </Button>
            )}
            {plan.enabled && (
              <Button variant="ghost" size="sm" disabled={busy} onClick={() => onOff(plan)}>
                {t('equipmentSafety.maintenance.switchOff')}
              </Button>
            )}
          </Inline>
          {plan.enabled && (
            <Link className="equipment-safety__link" to={`/aquariums/${aquariumId}/tasks`}>
              {t('equipmentSafety.maintenance.openTasks')}
            </Link>
          )}
        </Stack>
      </Card>
    </li>
  );
}

/**
 * "Wartungsplan" (Reef Guard #1828): editable maintenance templates. Switched
 * on, a template becomes a recurring task (category 'equipment'). The default
 * intervals are UNVERIFIED hobby heuristics and say so.
 */
export function MaintenancePlansSection({ aquariumId, safety }: { aquariumId: string; safety: EquipmentSafety }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [announcement, setAnnouncement] = useState('');
  // The card whose action finished last gets focus back on its heading.
  const [focus, setFocus] = useState<{ key: string; token: number }>({ key: '', token: 0 });
  const done = (data: EquipmentSafety | null, planKey: string, message: string | null) => {
    if (data) queryClient.setQueryData(equipmentSafetyKey(aquariumId), data);
    void queryClient.invalidateQueries({ queryKey: taskKeys.all });
    if (message) setAnnouncement(message);
    setFocus((f) => ({ key: planKey, token: f.token + 1 }));
  };
  const name = (plan: MaintenancePlan) => t(`equipmentSafety.maintenance.templates.${plan.key}.title`);
  const save = useMutation({
    mutationFn: ({ plan, days }: { plan: MaintenancePlan; days: number }) =>
      saveMaintenancePlan(aquariumId, plan.key, days, String(t(`equipmentSafety.maintenance.templates.${plan.key}.taskTitle`))),
    onSuccess: (data, { plan, days }) => done(data, plan.key, String(t('equipmentSafety.maintenance.announceOn', { name: name(plan), count: days }))),
    onError: (_err, { plan }) => done(null, plan.key, null),
  });
  const off = useMutation({
    mutationFn: (plan: MaintenancePlan) => deleteMaintenancePlan(aquariumId, plan.key),
    onSuccess: (data, plan) => done(data, plan.key, String(t('equipmentSafety.maintenance.announceOff', { name: name(plan) }))),
    onError: (_err, plan) => done(null, plan.key, null),
  });
  const busy = save.isPending || off.isPending;

  return (
    <Section id="maintenance" title={t('equipmentSafety.maintenance.title')} description={t('equipmentSafety.maintenance.description')}>
      <Stack gap="4">
        <LiveRegion visuallyHidden>{announcement}</LiveRegion>
        {(save.isError || off.isError) && <Alert tone="danger">{t('equipmentSafety.saveError')}</Alert>}
        {safety.maintenance.length === 0 ? (
          <Text color="muted">{t('equipmentSafety.empty')}</Text>
        ) : (
          <Grid as="ul" minItemWidth="md" gap="4" className="equipment-safety__list">
            {safety.maintenance.map((plan) => (
              <MaintenanceCard
                key={plan.key}
                plan={plan}
                aquariumId={aquariumId}
                busy={busy}
                focusToken={focus.key === plan.key ? focus.token : 0}
                onSave={(p, days) => save.mutate({ plan: p, days })}
                onOff={(p) => off.mutate(p)}
              />
            ))}
          </Grid>
        )}
        <Text color="muted" variant="caption">{t('equipmentSafety.maintenance.sourceNote')}</Text>
      </Stack>
    </Section>
  );
}
